//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import DoneIcon from '@mui/icons-material/Done'
import ButtonAction from '@react/components/elements/ButtonAction'
import { EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'

import { useOtChainState, useOtSelector } from '@react/redux/store'
import { textFieldValueToERC20DecString } from '@react/utils/textFieldsUtils'
import {
  commaDecStringToErc20Wei,
  commaDecStringToErcDecString,
  ERC20CommaDecString,
  ERC20DecString,
  toCommaDecString,
} from '@shared/conversions'
import { Contract } from 'ethers'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

import { fetchPoolParticipants } from '@react/fetchers/PoolFetchers'
import { Erc20Abi } from '@shared/Abis'
import { logOT } from '@shared/loggingShared'

export default function LenderTransferPoolTokens(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props

  const {
    isLoggedIn,
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { accountAddr, signer },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()

  const [showSpinnerAction2, setShowSpinnerAction2] = useState<boolean>(false)
  const [participants, setParticipants] = useState<PoolAccountAccessPair[]>([])

  const [validationMessage, setValidationMessage] = useState<string | undefined>(undefined)

  const [depositDone, setDepositDone] = useState<boolean>(false)
  const [value, setValue] = React.useState<ERC20DecString | ERC20CommaDecString>('')
  const [selectedParticipant, setSelectedParticipant] = React.useState<EthAddr>('')
  const theme = useTheme()

  const poolOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  useEffect(() => {
    if (accountAddr && poolAddr && serverApiBaseUrl && restAPIKey && providerReadOnly) {
      fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr).then((_participants) => {
        setParticipants(_participants)
      })
    }
  }, [providerReadOnly, accountAddr, poolAddr])

  const handleTokenTransfer = (): void => {
    async function tokenTransfer() {
      setShowSpinnerAction2(true)

      const amountWei = commaDecStringToErc20Wei(value)

      const poolErc20Contract = new Contract(poolOverviewCTO?.poolAddr as EthAddr, Erc20Abi, signer)
      if (accountAddr && poolAddr && signer) {
        await poolErc20Contract.transfer(selectedParticipant, amountWei)
      }
    }
    if (accountAddr && poolAddr) {
      tokenTransfer()
        .then(() => {
          setDepositDone(true)
          setShowSpinnerAction2(false)

          const event = new Event('modalClose')
          window.dispatchEvent(event)
        })
        .catch((err) => {
          logOT.info('approveTokenTransfer error', err)
        })
    }
  }

  const handleCancel = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const handleOnFocus = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValue(commaDecStringToErcDecString(value))
    checkValidation(commaDecStringToErc20Wei(value))
  }
  const handleOnBlur = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValue(toCommaDecString(value))
    checkValidation(commaDecStringToErc20Wei(value))
  }

  const checkValidation = (_amountWei: string) => {
    setValidationMessage(undefined)
  }
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValue(textFieldValueToERC20DecString(event))
    checkValidation(commaDecStringToErc20Wei(textFieldValueToERC20DecString(event)))
  }

  const header = 'Approve Deposit'
  const subHeader = ` ${poolOverviewCTO.liquidityTokenSymbol} Spending Cap Approved`

  const description = `Please input the amount that you’d like to deposit. After you approve, this amount of ${poolOverviewCTO.liquidityTokenSymbol} will be deposited into the Loan Vault.`

  const Header = () => (
    <>
      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', marginLeft: 1 }}>
        <Typography variant="body" align="left" margin="dense">
          {header}
        </Typography>
        <Typography variant="bodyBold" align="left" margin="dense">
          {subHeader}
        </Typography>
        <Typography variant="small" align="left" margin="dense" marginTop={3}>
          {description}
        </Typography>
      </Grid>
    </>
  )

  const actionTwoTitle = 'Approve the Deposit'
  const Actions = () => (
    <>
      <Grid container item marginTop={0} marginBottom={10} xs={12} key={'top'}>
        <Grid container item marginTop={10} xs={12} key={'dd1'}></Grid>
        <Grid container item marginTop={10} xs={12} key={'dd2'} marginLeft={2}>
          <Grid
            item
            xs={12}
            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}
          >
            {showSpinnerAction2 && <CircularProgress />}
            {!showSpinnerAction2 && depositDone && <DoneIcon sx={{ color: theme.palette.success.main }} />}
            {!showSpinnerAction2 && !depositDone && (
              <CheckBoxOutlineBlankIcon sx={{ color: theme.palette.success.main }} />
            )}
            <Typography variant="overline" margin="dense" sx={{ marginLeft: 4 }}>
              {actionTwoTitle}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  )

  const destinationSelect = (
    <FormControl sx={{ m: 1, width: 600 }}>
      <InputLabel id="asset-label">Lender</InputLabel>
      <Select
        style={{ width: 400 }}
        labelId="asset-label"
        id="asset-select"
        input={<OutlinedInput id="asset-select" label="Asset" />}
        value={selectedParticipant}
        renderValue={(value: string) => {
          return (
            <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
              {value}
            </Typography>
          )
        }}
        onChange={(event: SelectChangeEvent) => {
          logOT.info('event.target.value', event.target.value)
          setSelectedParticipant(event.target.value as string)
        }}
      >
        {participants.map((p: PoolAccountAccessPair) => (
          <MenuItem
            value={p.accountAddr}
            key={`asset-option-${p.accountAddr}`}
            style={{
              paddingLeft: 12,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
              {p.accountAddr}
            </Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    <Box py={2} px={1}>
      <Grid container spacing={2}>
        <Header />
        <Grid item xs={12} sx={{ marginLeft: 1, marginBottom: 0 }}>
          {destinationSelect}
        </Grid>
        <Grid item xs={12} sx={{ marginLeft: 1, marginBottom: 0 }}>
          <InputLabel htmlFor="amount">Investment ({poolOverviewCTO.liquidityTokenSymbol})</InputLabel>
          <TextField
            id="amountDeposit"
            error={!!validationMessage}
            helperText={validationMessage}
            data-testid="amountDeposit"
            sx={{ width: '300px' }}
            variant="outlined"
            placeholder="Enter Investment Amount"
            value={value}
            type={'tel'}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
          />
        </Grid>
        <Grid item xs={12}>
          <Actions />
          {isLoggedIn && selectedParticipant && (
            <ButtonAction
              disabled={!!validationMessage || value === '0'}
              data-testid="InvestButton"
              sx={{ marginRight: '16px', paddingLeft: '12px', paddingRight: '12px' }}
              variant={'primary'}
              onClick={handleTokenTransfer}
            >
              Transfer
            </ButtonAction>
          )}

          {!isLoggedIn && (
            <Typography variant="bodyBold" align="center" margin="dense">
              Please connect your wallet to deposit a pool
            </Typography>
          )}
          {(!showSpinnerAction2 || !showSpinnerAction2) && (
            <ButtonAction variant={'tertiary'} onClick={handleCancel}>
              Cancel
            </ButtonAction>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}
